import React from 'react';

import {ButtonRaw} from '@/components/Button';
import RemoteImage from '@/components/Image/RemoteImage';
import PageSection from '@/components/Layout/PageSection';
import appConfig from '@/config/appConfig.typed';
import {PUSH_NOTIFICATIONS_PROMPT_TYPES} from '@/consts/pushNotifications/pushNotificationsPromptTypes';
import useConfigExternalLink from '@/hooks/externalLink/useConfigExternalLink';
import usePageSectionTitle from '@/hooks/usePageSectionTitle';
import {getAriaLabel} from '@/services/accessibilityService';
import amplitudeShopPageTracking from '@/services/analytics/amplitude/amplitudeShopPageTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import {getLocalizedStrings} from '@/services/localization/localizationService';
import {filterSectionsById} from '@/services/pageSectionService';
import {schedulePushPromptIfNeeded} from '@/services/pushNotification/pushNotificationPromptService';
import arrayUtils from '@/utils/arrayUtils';

const getProducts = (id) => {
    const productsFromConfig = appConfig.getShopByProductSection();
    const products = filterSectionsById({list: productsFromConfig, id});
    const productsWithAction = products?.filter((p) => p?.action);

    if (!productsWithAction?.length) return null;

    const productsWithOrder = arrayUtils.sortArrayWithOrderField(
        productsWithAction?.filter((p) => p?.order !== undefined)
    );
    const productsWithoutOrder = productsWithAction.filter((p) => p?.order === undefined);

    return [...productsWithOrder, ...productsWithoutOrder];
};

const ShopByProductSection = ({id, className}) => {
    const products = getProducts(id);
    const localizedStrings = getLocalizedStrings();
    const titleText = usePageSectionTitle(id);

    if (!products?.length) return;

    return (
        <PageSection className={className}>
            <PageSection.Title text={titleText} />
            <div className='mt-4 grid grid-cols-2 gap-x-2 gap-y-4 md:mt-6 md:gap-4 ml:mt-6 ml:grid-cols-4 ml:gap-x-4'>
                {products.map(({imageMedia, titleKey, action, amplitudeTrackingValue, actionAriaLabelKey}, index) => (
                    <ShopByProductTile
                        imageMedia={imageMedia}
                        title={localizedStrings[titleKey]}
                        action={action}
                        actionAriaLabelKey={actionAriaLabelKey}
                        amplitudeTrackingValue={amplitudeTrackingValue}
                        key={index}
                    />
                ))}
            </div>
        </PageSection>
    );
};

const ShopByProductTile = ({imageMedia, title, action, actionAriaLabelKey, amplitudeTrackingValue}) => {
    const {redirectToExternalLink} = useConfigExternalLink(action);
    const onTileClick = () => {
        schedulePushPromptIfNeeded(PUSH_NOTIFICATIONS_PROMPT_TYPES.URL_OPEN_ON_SHOP_PAGE);
        amplitudeShopPageTracking.trackShopByProductBannerClick(amplitudeTrackingValue);
        googleAnalyticsService.trackShopByProductBannerClick(amplitudeTrackingValue);
        redirectToExternalLink();
    };

    return (
        <ButtonRaw
            className='flex max-w-[250px] flex-col items-center justify-between gap-4 rounded-xl bg-primary-dark-5 p-4 md:w-full md:max-w-none md:rounded-3xl md:bg-romance md:p-8'
            onClick={action && onTileClick}
            aria-label={getAriaLabel(actionAriaLabelKey)}
        >
            <span className='line-clamp-2 w-full shrink-0 text-14 font-bold leading-155 text-black md:text-24 ml:leading-15 lg:leading-155'>
                {title}
            </span>
            <RemoteImage className='h-full object-contain md:h-[233px]' imageMedia={imageMedia} />
        </ButtonRaw>
    );
};

export default ShopByProductSection;
